import type { WorkbenchCrossTextAlignmentTextInput } from '../api/workbenchCrossTextTraditionAlignmentAPI'
import type { CrossTextAlignmentCandidate, CrossTextAlignmentRun, CrossTextSourceSegment } from './crossTextTraditionAlignmentReview'
import { buildWitnessUnitTextIndex, preprocessLedgerText, type WitnessUnitTextRecord } from './ledgerTextPreprocessing'
import type { Book, Chapter, Witness } from '../store/booksStore'
import type { CompleteMicroSegmentationResult, MicroUnit, TextFile } from '../types/segmentationTypes'
import { getRowLabel, type AnchoredRow, type UnitMapping } from '../types/anchoredRowTypes'

export const LEDGER_CROSS_TRADITION_RERANK_MODEL_ID = 'gemini-3.1-flash-lite'
export const LEDGER_CROSS_TRADITION_SCORER = 'tiered-gemini-windowed'
export const LEDGER_CROSS_TRADITION_ROW_SPINE_TEXT_ID_PREFIX = 'ledger-row-spine'
export const LEDGER_CROSS_TRADITION_METHOD = 'ledger_cross_tradition_collation'
export const LEDGER_CROSS_TRADITION_PROFILE_OWNER = 'active-ledger-collation'

export type LedgerCrossTraditionTextLookupByWitnessId = Map<string, Map<string, WitnessUnitTextRecord>>

export interface LedgerCrossTraditionSourceOption {
  textId: string
  workId: string
  workTitle: string
  chapterId: string
  chapterTitle: string
  witnessId: string
  sigla: string
  label: string
  contextLabel: string
  witness: Witness
}

export interface LedgerCrossTraditionSourceSegmentRef {
  segmentIndex: number
  unitUuid?: string
  unitId?: string
  unitLabel?: string
  text: string
}

export interface LedgerCrossTraditionSourceText {
  text: WorkbenchCrossTextAlignmentTextInput
  segmentRefs: LedgerCrossTraditionSourceSegmentRef[]
}

export interface LedgerCrossTraditionRowSpineText {
  text: WorkbenchCrossTextAlignmentTextInput
  rowIdsBySegmentIndex: string[]
  rowLabelsBySegmentIndex: string[]
}

export interface LedgerCrossTraditionCandidate {
  candidateId: string
  previewId: string
  modelId: string
  rerankModelId?: string | null
  sourceWitnessId: string
  sourceTextId: string
  sourceLabel: string
  sourceContextLabel: string | null
  sourceSegmentStart: number
  sourceSegmentEnd: number
  sourceText: string
  sourceUnitUuids: string[]
  sourceSegments: LedgerCrossTraditionSourceSegmentRef[]
  targetRowIds: string[]
  targetRowLabels: string[]
  targetText: string
  score: number
  scoreMargin: number
  rank: number
  tier: CrossTextAlignmentCandidate['tier']
  perspectiveMode: string
  reviewStatus: CrossTextAlignmentCandidate['reviewStatus']
  rerankVerdict?: string | null
  rerankConfidence?: number | null
  rerankReason?: string | null
}

export interface LedgerCrossTraditionActiveProfile {
  profileId: string
  projectionKey: string
  owner: typeof LEDGER_CROSS_TRADITION_PROFILE_OWNER
  targetBookId?: string | null
  targetChapterId?: string | null
  targetContextLabel?: string | null
  activeCollationLabel?: string | null
  rowSpineTextId?: string | null
  sourceWorkId: string
  sourceChapterId: string
  sourceWitnessId: string
  sourceContextLabel: string
  modelId: string
  rerankModelId?: string | null
  acceptedCandidateIds: string[]
  createdAt: number
  updatedAt?: number
}

export interface LedgerCrossTraditionProfileUnit {
  candidateId: string
  targetRowId: string
  targetRowLabel: string
  unitUuid: string
  unitLabel: string
  text: string
  sourceUnitUuids: string[]
  sourceSegmentStart: number
  sourceSegmentEnd: number
  splitMethod: 'one-to-one' | 'source-segment-group' | 'text-window-slice' | 'span-context'
  needsBoundaryReview: boolean
  score?: number
  previewId?: string | null
  modelId?: string | null
  rerankModelId?: string | null
  rerankVerdict?: string | null
  targetRowIds?: string[]
}

export interface LedgerCrossTraditionWitnessImportPlan {
  sourceOption: LedgerCrossTraditionSourceOption
  profile: LedgerCrossTraditionActiveProfile
  projectionKey: string
  existingProjectionWitnessId?: string | null
  placeholderSigla: string
  witnessData: Omit<Witness, 'id' | 'chapter_id' | 'book_id' | 'created_at' | 'processing_status'>
  unitByCandidateId: Map<string, { unitUuid: string; unitLabel: string; text: string }>
  unitByCandidateRowKey: Map<string, LedgerCrossTraditionProfileUnit>
  acceptedCandidateIds: string[]
}

export interface LedgerCrossTraditionRowApplication {
  updatedRows: AnchoredRow[]
  changedRowIds: string[]
  appliedCandidateIds: string[]
}

const clean = (value: string | null | undefined): string => String(value || '').trim()

const makeStableIdPart = (value: string): string => (
  clean(value)
    .toLowerCase()
    .replace(/[^a-z0-9\u0600-\u06ff]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'x'
)

const newId = (prefix: string): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

const stableHash = (value: string): string => {
  let hash = 2166136261
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }
  return (hash >>> 0).toString(36)
}

const asRecord = (value: unknown): Record<string, unknown> | null => (
  value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null
)

const stringValue = (value: unknown): string | null => {
  const text = clean(typeof value === 'string' || typeof value === 'number' ? String(value) : '')
  return text || null
}

const numberValue = (value: unknown): number | null => (
  typeof value === 'number' && Number.isFinite(value) ? value : null
)

const stringArrayValue = (value: unknown): string[] => (
  Array.isArray(value)
    ? value.map(entry => stringValue(entry)).filter((entry): entry is string => Boolean(entry))
    : []
)

const isLedgerCrossTraditionSplitMethod = (
  value: unknown
): value is LedgerCrossTraditionProfileUnit['splitMethod'] => (
  value === 'one-to-one'
  || value === 'source-segment-group'
  || value === 'text-window-slice'
  || value === 'span-context'
)

export const buildLedgerCrossTraditionProjectionKey = ({
  targetBookId = null,
  targetChapterId = null,
  activeCollationLabel = null,
  rowSpineTextId = null,
  sourceWorkId,
  sourceChapterId,
  sourceWitnessId,
}: {
  targetBookId?: string | null
  targetChapterId?: string | null
  activeCollationLabel?: string | null
  rowSpineTextId?: string | null
  sourceWorkId: string
  sourceChapterId: string
  sourceWitnessId: string
}): string => {
  const payload = [
    LEDGER_CROSS_TRADITION_PROFILE_OWNER,
    clean(targetBookId) || 'target-book',
    clean(targetChapterId) || 'target-chapter',
    clean(activeCollationLabel) || clean(rowSpineTextId) || 'active-collation',
    clean(sourceWorkId) || 'source-work',
    clean(sourceChapterId) || 'source-chapter',
    clean(sourceWitnessId) || 'source-witness',
  ].join('\u0000')
  return `xtrad-projection-${stableHash(payload)}`
}

const getWitnessSegmentationMetadata = (witness: Witness | null | undefined): Record<string, unknown> | null => (
  asRecord(witness?.segmentation_result?.segmentation_metadata)
)

const getWitnessActiveCrossTraditionProfile = (
  witness: Witness | null | undefined
): Partial<LedgerCrossTraditionActiveProfile> | null => {
  const metadata = getWitnessSegmentationMetadata(witness)
  const activeProfile = asRecord(metadata?.active_profile)
  return activeProfile as Partial<LedgerCrossTraditionActiveProfile> | null
}

export const getLedgerCrossTraditionProjectionKeyFromWitness = (
  witness: Witness | null | undefined
): string | null => {
  const metadata = getWitnessSegmentationMetadata(witness)
  const activeProfile = getWitnessActiveCrossTraditionProfile(witness)
  const method = stringValue(metadata?.method)
  const owner = stringValue(metadata?.profile_owner) || stringValue(activeProfile?.owner)
  if (method && method !== LEDGER_CROSS_TRADITION_METHOD) {
    return null
  }
  if (owner && owner !== LEDGER_CROSS_TRADITION_PROFILE_OWNER) {
    return null
  }

  const explicitKey = stringValue(metadata?.projection_key) || stringValue(activeProfile?.projectionKey)
  if (explicitKey) {
    return explicitKey
  }

  const sourceWorkId = stringValue(metadata?.source_work_id) || stringValue(activeProfile?.sourceWorkId)
  const sourceChapterId = stringValue(metadata?.source_chapter_id) || stringValue(activeProfile?.sourceChapterId)
  const sourceWitnessId = stringValue(metadata?.source_witness_id) || stringValue(activeProfile?.sourceWitnessId)
  if (!sourceWorkId || !sourceChapterId || !sourceWitnessId) {
    return null
  }

  return buildLedgerCrossTraditionProjectionKey({
    targetBookId: stringValue(metadata?.target_book_id) || stringValue(activeProfile?.targetBookId),
    targetChapterId: stringValue(metadata?.target_chapter_id) || stringValue(activeProfile?.targetChapterId),
    activeCollationLabel: stringValue(metadata?.active_collation_label) || stringValue(activeProfile?.activeCollationLabel),
    rowSpineTextId: stringValue(metadata?.row_spine_text_id) || stringValue(activeProfile?.rowSpineTextId),
    sourceWorkId,
    sourceChapterId,
    sourceWitnessId,
  })
}

export const findLedgerCrossTraditionProjectionWitness = (
  witnesses: Witness[],
  projectionKey: string
): Witness | null => (
  witnesses.find(witness => getLedgerCrossTraditionProjectionKeyFromWitness(witness) === projectionKey) || null
)

export const ledgerCrossTraditionCandidateRowKey = (candidateId: string, rowId: string): string => (
  `${candidateId}::${rowId}`
)

const getOrderedUnits = (segmentationResult: CompleteMicroSegmentationResult | null | undefined): MicroUnit[] => {
  const units = (segmentationResult?.chunk_results || [])
    .flatMap(chunk => chunk.micro_units || [])
  return units.slice().sort((left, right) => {
    const leftStart = typeof left.start_position === 'number' ? left.start_position : Number.MAX_SAFE_INTEGER
    const rightStart = typeof right.start_position === 'number' ? right.start_position : Number.MAX_SAFE_INTEGER
    if (leftStart !== rightStart) {
      return leftStart - rightStart
    }
    return String(left.unit_id || left.unit_uuid || '').localeCompare(String(right.unit_id || right.unit_uuid || ''))
  })
}

const splitFallbackTextSegments = (content: string): string[] => {
  const lines = content
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map(line => clean(line))
    .filter(Boolean)
  if (lines.length > 1) {
    return lines
  }
  const text = clean(lines[0] || content)
  if (!text) {
    return []
  }
  const sentenceParts = text.split(/(?<=[.؟?!؛])\s+/).map(part => clean(part)).filter(Boolean)
  if (sentenceParts.length > 1) {
    return sentenceParts
  }
  return Array.from({ length: Math.ceil(text.length / 360) }, (_, index) => clean(text.slice(index * 360, (index + 1) * 360))).filter(Boolean)
}

const splitTextIntoWindows = (text: string, count: number): string[] => {
  const cleaned = clean(text)
  if (count <= 1 || !cleaned) {
    return cleaned ? [cleaned] : []
  }

  const lines = cleaned
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map(line => clean(line))
    .filter(Boolean)
  if (lines.length >= count) {
    return Array.from({ length: count }, (_, index) => {
      const start = Math.floor(index * lines.length / count)
      const end = Math.floor((index + 1) * lines.length / count)
      return lines.slice(start, Math.max(start + 1, end)).join('\n')
    }).filter(Boolean)
  }

  const sentenceParts = cleaned.split(/(?<=[.؟?!؛])\s+/).map(part => clean(part)).filter(Boolean)
  if (sentenceParts.length >= count) {
    return Array.from({ length: count }, (_, index) => {
      const start = Math.floor(index * sentenceParts.length / count)
      const end = Math.floor((index + 1) * sentenceParts.length / count)
      return sentenceParts.slice(start, Math.max(start + 1, end)).join(' ')
    }).filter(Boolean)
  }

  const tokens = cleaned.split(/\s+/).filter(Boolean)
  if (tokens.length >= count) {
    return Array.from({ length: count }, (_, index) => {
      const start = Math.floor(index * tokens.length / count)
      const end = Math.floor((index + 1) * tokens.length / count)
      return tokens.slice(start, Math.max(start + 1, end)).join(' ')
    }).filter(Boolean)
  }

  return [cleaned]
}

const targetRowEntriesForCandidate = (candidate: LedgerCrossTraditionCandidate): Array<{ rowId: string; rowLabel: string }> => (
  candidate.targetRowIds.map((rowId, index) => ({
    rowId,
    rowLabel: candidate.targetRowLabels[index] || rowId,
  }))
)

const sourceSegmentsForCandidate = (candidate: LedgerCrossTraditionCandidate): LedgerCrossTraditionSourceSegmentRef[] => {
  const segments = (candidate.sourceSegments || []).length > 0
    ? candidate.sourceSegments
    : splitFallbackTextSegments(candidate.sourceText).map((text, index) => ({
      segmentIndex: candidate.sourceSegmentStart + index,
      unitUuid: candidate.sourceUnitUuids[index],
      text,
    }))
  return segments.length > 0
    ? segments
    : [{ segmentIndex: candidate.sourceSegmentStart, text: candidate.sourceText }]
}

const buildProfileUnitUuid = ({
  sourceWitnessId,
  candidateId,
  rowId,
}: {
  sourceWitnessId: string
  candidateId: string
  rowId: string
}): string => (
  `cross-tradition-unit-${stableHash([
    clean(sourceWitnessId),
    clean(candidateId),
    clean(rowId),
  ].join('\u0000'))}`
)

const allocateProfileUnitsForCandidate = ({
  candidate,
  placeholderSigla,
  sourceWitnessId,
  startUnitNumber,
}: {
  candidate: LedgerCrossTraditionCandidate
  placeholderSigla: string
  sourceWitnessId: string
  startUnitNumber: number
}): LedgerCrossTraditionProfileUnit[] => {
  const targetRows = targetRowEntriesForCandidate(candidate)
  const sourceSegments = sourceSegmentsForCandidate(candidate)
  if (targetRows.length === 0) {
    return []
  }

  const makeUnit = (
    row: { rowId: string; rowLabel: string },
    unitNumber: number,
    text: string,
    sourceRefs: LedgerCrossTraditionSourceSegmentRef[],
    splitMethod: LedgerCrossTraditionProfileUnit['splitMethod'],
    needsBoundaryReview: boolean,
  ): LedgerCrossTraditionProfileUnit => {
    const unitUuid = buildProfileUnitUuid({
      sourceWitnessId,
      candidateId: candidate.candidateId,
      rowId: row.rowId,
    })
    return {
      candidateId: candidate.candidateId,
      targetRowId: row.rowId,
      targetRowLabel: row.rowLabel,
      unitUuid,
      unitLabel: `${placeholderSigla}.${unitNumber}`,
      text,
      sourceUnitUuids: sourceRefs.map(ref => ref.unitUuid || '').filter(Boolean),
      sourceSegmentStart: sourceRefs[0]?.segmentIndex ?? candidate.sourceSegmentStart,
      sourceSegmentEnd: sourceRefs[sourceRefs.length - 1]?.segmentIndex ?? candidate.sourceSegmentEnd,
      splitMethod,
      needsBoundaryReview,
      score: candidate.score,
      previewId: candidate.previewId,
      modelId: candidate.modelId,
      rerankModelId: candidate.rerankModelId || null,
      rerankVerdict: candidate.rerankVerdict || null,
      targetRowIds: candidate.targetRowIds,
    }
  }

  if (sourceSegments.length === targetRows.length) {
    return targetRows.map((row, index) => makeUnit(
      row,
      startUnitNumber + index,
      sourceSegments[index].text,
      [sourceSegments[index]],
      'one-to-one',
      candidate.targetRowIds.length > 1,
    ))
  }

  if (sourceSegments.length > targetRows.length) {
    return targetRows.map((row, index) => {
      const start = Math.floor(index * sourceSegments.length / targetRows.length)
      const end = Math.floor((index + 1) * sourceSegments.length / targetRows.length)
      const refs = sourceSegments.slice(start, Math.max(start + 1, end))
      return makeUnit(
        row,
        startUnitNumber + index,
        refs.map(ref => ref.text).join('\n'),
        refs,
        'source-segment-group',
        true,
      )
    })
  }

  const textSlices = splitTextIntoWindows(candidate.sourceText, targetRows.length)
  if (textSlices.length === targetRows.length) {
    return targetRows.map((row, index) => makeUnit(
      row,
      startUnitNumber + index,
      textSlices[index],
      sourceSegments,
      'text-window-slice',
      true,
    ))
  }

  return [
    makeUnit(
      targetRows[0],
      startUnitNumber,
      candidate.sourceText,
      sourceSegments,
      'span-context',
      true,
    ),
  ]
}

const extractLedgerCrossTraditionProfileUnits = (
  witness: Witness | null | undefined
): LedgerCrossTraditionProfileUnit[] => {
  const metadata = getWitnessSegmentationMetadata(witness)
  const method = stringValue(metadata?.method)
  const owner = stringValue(metadata?.profile_owner)
  if (
    (method && method !== LEDGER_CROSS_TRADITION_METHOD)
    || (owner && owner !== LEDGER_CROSS_TRADITION_PROFILE_OWNER)
  ) {
    return []
  }

  return getOrderedUnits(witness?.segmentation_result)
    .map((unit): LedgerCrossTraditionProfileUnit | null => {
      const processing = asRecord(unit.processing_metadata)
      const candidateId = stringValue(processing?.candidate_id)
      const targetRowId = stringValue(processing?.target_row_id)
      if (!candidateId || !targetRowId) {
        return null
      }
      const sourceSegmentStart = numberValue(processing?.source_segment_start)
      const sourceSegmentEnd = numberValue(processing?.source_segment_end)
      const rawSplitMethod = processing?.split_method
      const splitMethod = isLedgerCrossTraditionSplitMethod(rawSplitMethod)
        ? rawSplitMethod
        : 'span-context'
      return {
        candidateId,
        targetRowId,
        targetRowLabel: stringValue(processing?.target_row_label) || stringValue(unit.unit_label) || targetRowId,
        unitUuid: stringValue(unit.unit_uuid) || stringValue(unit.unit_id) || buildProfileUnitUuid({
          sourceWitnessId: stringValue(processing?.source_witness_id) || stringValue(metadata?.source_witness_id) || 'source-witness',
          candidateId,
          rowId: targetRowId,
        }),
        unitLabel: stringValue(unit.unit_label) || targetRowId,
        text: clean(unit.unit_text_content),
        sourceUnitUuids: stringArrayValue(processing?.source_unit_uuids),
        sourceSegmentStart: sourceSegmentStart ?? 0,
        sourceSegmentEnd: sourceSegmentEnd ?? sourceSegmentStart ?? 0,
        splitMethod,
        needsBoundaryReview: Boolean(processing?.needs_boundary_review),
        score: typeof unit.confidence_score === 'number' ? unit.confidence_score : undefined,
        previewId: stringValue(processing?.preview_id),
        modelId: stringValue(processing?.model_id),
        rerankModelId: stringValue(processing?.rerank_model_id),
        rerankVerdict: stringValue(processing?.rerank_verdict),
        targetRowIds: stringArrayValue(processing?.target_row_ids),
      }
    })
    .filter((unit): unit is LedgerCrossTraditionProfileUnit => Boolean(unit))
}

const sourceOptionSortKey = (option: LedgerCrossTraditionSourceOption): string => [
  option.workTitle,
  option.chapterTitle,
  option.sigla,
  option.witnessId,
].join('\u0000')

export const buildLedgerCrossTraditionSourceOptions = ({
  books,
  chapters,
  witnesses,
  currentBookId,
  currentChapterId,
}: {
  books: Book[]
  chapters: Chapter[]
  witnesses: Witness[]
  currentBookId: string
  currentChapterId: string
}): LedgerCrossTraditionSourceOption[] => {
  const bookById = new Map(books.map(book => [book.id, book]))
  const chapterById = new Map(chapters.map(chapter => [chapter.id, chapter]))

  return witnesses
    .map((witness): LedgerCrossTraditionSourceOption | null => {
      if (witness.chapter_id === currentChapterId || witness.book_id === currentBookId) {
        return null
      }
      const book = bookById.get(witness.book_id)
      const chapter = chapterById.get(witness.chapter_id)
      if (!book || !chapter || book.archived || chapter.archived) {
        return null
      }
      const hasText = Boolean(clean(witness.uploaded_file?.content)) || getOrderedUnits(witness.segmentation_result).length > 0
      if (!hasText) {
        return null
      }
      const workTitle = clean(book.name) || witness.book_id
      const chapterTitle = clean(chapter.name) || witness.chapter_id
      const sigla = clean(witness.sigla) || witness.id
      return {
        textId: `cross-tradition-source:${witness.id}`,
        workId: book.id,
        workTitle,
        chapterId: chapter.id,
        chapterTitle,
        witnessId: witness.id,
        sigla,
        label: `${sigla} · ${workTitle}`,
        contextLabel: `${workTitle} / ${chapterTitle}`,
        witness,
      }
    })
    .filter((option): option is LedgerCrossTraditionSourceOption => Boolean(option))
    .sort((left, right) => sourceOptionSortKey(left).localeCompare(sourceOptionSortKey(right)))
}

export const buildLedgerCrossTraditionSourceText = (
  option: LedgerCrossTraditionSourceOption,
): LedgerCrossTraditionSourceText => {
  const unitTextIndex = buildWitnessUnitTextIndex(option.witness)
  const orderedUnits = getOrderedUnits(option.witness.segmentation_result)
  const segmentRefs: LedgerCrossTraditionSourceSegmentRef[] = []
  let segments: CrossTextSourceSegment[] = []

  if (orderedUnits.length > 0) {
    const nextSegments: CrossTextSourceSegment[] = []
    orderedUnits.forEach(unit => {
      const unitUuid = clean(unit.unit_uuid || unit.unit_id)
      const indexed = unitUuid ? unitTextIndex.get(unitUuid) : null
      const text = clean(indexed?.readingText || indexed?.workspaceDisplayText || unit.unit_text_content)
      if (!text) {
        return
      }
      const segmentIndex = nextSegments.length
      segmentRefs.push({
        segmentIndex,
        unitUuid: unitUuid || undefined,
        unitId: clean(unit.unit_id) || undefined,
        unitLabel: clean(unit.unit_label) || undefined,
        text,
      })
      nextSegments.push({ index: segmentIndex, text })
    })
    segments = nextSegments
  }

  if (segments.length === 0) {
    segments = splitFallbackTextSegments(option.witness.uploaded_file?.content || '').map((text, index) => {
      segmentRefs.push({ segmentIndex: index, text })
      return { index, text }
    })
  }

  const content = segments.map(segment => segment.text).join('\n')
  return {
    text: {
      textId: option.textId,
      label: option.label,
      contextLabel: option.contextLabel,
      content,
      segments,
    },
    segmentRefs,
  }
}

export const buildLedgerCrossTraditionRowSpineText = ({
  chapterId,
  contextLabel,
  rows,
  witnesses,
  textLookupByWitnessId,
}: {
  chapterId: string
  contextLabel?: string | null
  rows: AnchoredRow[]
  witnesses: Witness[]
  textLookupByWitnessId: LedgerCrossTraditionTextLookupByWitnessId
}): LedgerCrossTraditionRowSpineText => {
  const rowIdsBySegmentIndex: string[] = []
  const rowLabelsBySegmentIndex: string[] = []
  const segments: CrossTextSourceSegment[] = []

  rows
    .slice()
    .sort((left, right) => left.position - right.position)
    .forEach(row => {
      const rowTexts: string[] = []
      for (const witness of witnesses) {
        const mapping = row.unitMappings.find(candidate => candidate.witnessId === witness.id && !candidate.excluded)
        if (!mapping) {
          continue
        }
        const lookup = textLookupByWitnessId.get(witness.id)
        const indexed = lookup?.get(mapping.unitUuid)
        const fallback = mapping.textSnapshot ? preprocessLedgerText(mapping.textSnapshot).readingText : ''
        const text = clean(indexed?.readingText || indexed?.workspaceDisplayText || fallback)
        if (text && !rowTexts.includes(text)) {
          rowTexts.push(text)
        }
        if (rowTexts.length >= 4) {
          break
        }
      }
      if (rowTexts.length === 0) {
        return
      }
      const segmentIndex = segments.length
      rowIdsBySegmentIndex.push(row.id)
      rowLabelsBySegmentIndex.push(getRowLabel(row))
      segments.push({
        index: segmentIndex,
        text: `${getRowLabel(row)} ${rowTexts.join(' || ')}`,
      })
    })

  const textId = `${LEDGER_CROSS_TRADITION_ROW_SPINE_TEXT_ID_PREFIX}:${chapterId}`
  return {
    text: {
      textId,
      label: 'Accepted Ledger rows',
      contextLabel: contextLabel || null,
      content: segments.map(segment => segment.text).join('\n'),
      segments,
    },
    rowIdsBySegmentIndex,
    rowLabelsBySegmentIndex,
  }
}

export const mapCrossTextRunToLedgerCrossTraditionCandidates = ({
  run,
  sourceOption,
  sourceSegmentRefs,
  rowSpine,
}: {
  run: CrossTextAlignmentRun
  sourceOption: LedgerCrossTraditionSourceOption
  sourceSegmentRefs: LedgerCrossTraditionSourceSegmentRef[]
  rowSpine: LedgerCrossTraditionRowSpineText
}): LedgerCrossTraditionCandidate[] => {
  const sourceRefBySegmentIndex = new Map(sourceSegmentRefs.map(ref => [ref.segmentIndex, ref]))
  return run.candidates
    .filter(candidate => candidate.targetTextId === rowSpine.text.textId)
    .map(candidate => {
      const targetRowIds = rowSpine.rowIdsBySegmentIndex.slice(candidate.targetSegmentStart, candidate.targetSegmentEnd + 1)
      const targetRowLabels = rowSpine.rowLabelsBySegmentIndex.slice(candidate.targetSegmentStart, candidate.targetSegmentEnd + 1)
      const sourceSegments = Array.from({ length: candidate.sourceSegmentEnd - candidate.sourceSegmentStart + 1 }, (_, offset) => {
        const segmentIndex = candidate.sourceSegmentStart + offset
        return sourceRefBySegmentIndex.get(segmentIndex) || {
          segmentIndex,
          text: '',
        }
      }).filter(ref => clean(ref.text))
      const sourceUnitUuids = sourceSegments.map(ref => ref.unitUuid || '').filter(Boolean)
      return {
        candidateId: candidate.candidateId,
        previewId: run.previewId,
        modelId: run.modelId,
        rerankModelId: run.rerankModelId || candidate.rerankModelId || null,
        sourceWitnessId: sourceOption.witnessId,
        sourceTextId: candidate.sourceTextId,
        sourceLabel: sourceOption.sigla,
        sourceContextLabel: sourceOption.contextLabel,
        sourceSegmentStart: candidate.sourceSegmentStart,
        sourceSegmentEnd: candidate.sourceSegmentEnd,
        sourceText: candidate.sourceText,
        sourceUnitUuids,
        sourceSegments,
        targetRowIds,
        targetRowLabels,
        targetText: candidate.targetText,
        score: candidate.score,
        scoreMargin: candidate.scoreMargin,
        rank: candidate.rank,
        tier: candidate.tier,
        perspectiveMode: candidate.perspectiveMode,
        reviewStatus: candidate.reviewStatus,
        rerankVerdict: candidate.rerankVerdict || null,
        rerankConfidence: candidate.rerankConfidence ?? null,
        rerankReason: candidate.rerankReason || null,
      }
    })
    .filter(candidate => candidate.targetRowIds.length > 0)
}

export const setLedgerCrossTraditionCandidateStatus = (
  candidates: LedgerCrossTraditionCandidate[],
  candidateId: string,
  reviewStatus: LedgerCrossTraditionCandidate['reviewStatus'],
): LedgerCrossTraditionCandidate[] => candidates.map(candidate => (
  candidate.candidateId === candidateId ? { ...candidate, reviewStatus } : candidate
))

const uniquePlaceholderSigla = (baseSigla: string, existingWitnesses: Witness[]): string => {
  const existing = new Set(existingWitnesses.map(witness => clean(witness.sigla).toLowerCase()))
  const base = `XT-${makeStableIdPart(baseSigla).toUpperCase()}`
  if (!existing.has(base.toLowerCase())) {
    return base
  }
  for (let index = 2; index < 1000; index += 1) {
    const candidate = `${base}-${index}`
    if (!existing.has(candidate.toLowerCase())) {
      return candidate
    }
  }
  return `${base}-${Date.now()}`
}

export const buildLedgerCrossTraditionWitnessImportPlan = ({
  sourceOption,
  acceptedCandidates,
  existingWitnesses,
  targetBookId = null,
  targetChapterId = null,
  targetContextLabel = null,
  activeCollationLabel = null,
  rowSpineTextId = null,
  now = Date.now(),
}: {
  sourceOption: LedgerCrossTraditionSourceOption
  acceptedCandidates: LedgerCrossTraditionCandidate[]
  existingWitnesses: Witness[]
  targetBookId?: string | null
  targetChapterId?: string | null
  targetContextLabel?: string | null
  activeCollationLabel?: string | null
  rowSpineTextId?: string | null
  now?: number
}): LedgerCrossTraditionWitnessImportPlan => {
  const orderedCandidates = acceptedCandidates
    .filter(candidate => candidate.sourceWitnessId === sourceOption.witnessId && candidate.reviewStatus === 'accepted')
    .slice()
    .sort((left, right) => {
      if (left.sourceSegmentStart !== right.sourceSegmentStart) {
        return left.sourceSegmentStart - right.sourceSegmentStart
      }
      return left.targetRowLabels.join(',').localeCompare(right.targetRowLabels.join(','))
    })
  const projectionKey = buildLedgerCrossTraditionProjectionKey({
    targetBookId,
    targetChapterId,
    activeCollationLabel,
    rowSpineTextId,
    sourceWorkId: sourceOption.workId,
    sourceChapterId: sourceOption.chapterId,
    sourceWitnessId: sourceOption.witnessId,
  })
  const existingProjectionWitness = findLedgerCrossTraditionProjectionWitness(existingWitnesses, projectionKey)
  const existingProfile = getWitnessActiveCrossTraditionProfile(existingProjectionWitness)
  const placeholderSigla = clean(existingProjectionWitness?.sigla) || uniquePlaceholderSigla(sourceOption.sigla, existingWitnesses)
  const acceptedCandidateIds = Array.from(new Set([
    ...stringArrayValue(existingProfile?.acceptedCandidateIds),
    ...orderedCandidates.map(candidate => candidate.candidateId),
  ]))
  const profileId = `xtrad-profile-${projectionKey}`
  const profile: LedgerCrossTraditionActiveProfile = {
    profileId,
    projectionKey,
    owner: LEDGER_CROSS_TRADITION_PROFILE_OWNER,
    targetBookId,
    targetChapterId,
    targetContextLabel,
    activeCollationLabel,
    rowSpineTextId: rowSpineTextId || null,
    sourceWorkId: sourceOption.workId,
    sourceChapterId: sourceOption.chapterId,
    sourceWitnessId: sourceOption.witnessId,
    sourceContextLabel: sourceOption.contextLabel,
    modelId: orderedCandidates[0]?.modelId || 'unknown',
    rerankModelId: orderedCandidates[0]?.rerankModelId || LEDGER_CROSS_TRADITION_RERANK_MODEL_ID,
    acceptedCandidateIds,
    createdAt: numberValue(existingProfile?.createdAt) ?? now,
    updatedAt: now,
  }
  const candidateById = new Map(orderedCandidates.map(candidate => [candidate.candidateId, candidate]))
  const profileUnitByCandidateRowKey = new Map<string, LedgerCrossTraditionProfileUnit>()
  const orderedProfileUnits: LedgerCrossTraditionProfileUnit[] = []
  extractLedgerCrossTraditionProfileUnits(existingProjectionWitness).forEach(profileUnit => {
    const key = ledgerCrossTraditionCandidateRowKey(profileUnit.candidateId, profileUnit.targetRowId)
    if (profileUnitByCandidateRowKey.has(key)) {
      return
    }
    profileUnitByCandidateRowKey.set(key, profileUnit)
    orderedProfileUnits.push(profileUnit)
  })

  orderedCandidates.forEach(candidate => {
    const profileUnits = allocateProfileUnitsForCandidate({
      candidate,
      placeholderSigla,
      sourceWitnessId: sourceOption.witnessId,
      startUnitNumber: orderedProfileUnits.length + 1,
    })
    profileUnits.forEach(profileUnit => {
      const key = ledgerCrossTraditionCandidateRowKey(profileUnit.candidateId, profileUnit.targetRowId)
      if (profileUnitByCandidateRowKey.has(key)) {
        return
      }
      profileUnitByCandidateRowKey.set(key, profileUnit)
      orderedProfileUnits.push(profileUnit)
    })
  })

  const unitByCandidateId = new Map<string, { unitUuid: string; unitLabel: string; text: string }>()
  const unitByCandidateRowKey = new Map<string, LedgerCrossTraditionProfileUnit>()
  const contentParts: string[] = []
  const microUnits: MicroUnit[] = []
  let cursor = 0

  orderedProfileUnits.forEach(profileUnit => {
    const candidate = candidateById.get(profileUnit.candidateId)
    if (contentParts.length > 0) {
      contentParts.push('\n\n')
      cursor += 2
    }
    const text = clean(profileUnit.text)
    const start = cursor
    const end = start + text.length
    const unitNumber = microUnits.length + 1
    const unitId = `${placeholderSigla}_U${unitNumber}`
    contentParts.push(text)
    cursor = end
    if (!unitByCandidateId.has(profileUnit.candidateId)) {
      unitByCandidateId.set(profileUnit.candidateId, {
        unitUuid: profileUnit.unitUuid,
        unitLabel: profileUnit.unitLabel,
        text,
      })
    }
    unitByCandidateRowKey.set(ledgerCrossTraditionCandidateRowKey(profileUnit.candidateId, profileUnit.targetRowId), profileUnit)
    microUnits.push({
      unit_id: unitId,
      unit_uuid: profileUnit.unitUuid,
      unit_number: unitNumber,
      unit_label: profileUnit.unitLabel,
      source_chunk_id: 'cross_tradition_accepted_links',
      parent_primary_chunk: 'cross_tradition_accepted_links',
      unit_type: profileUnit.needsBoundaryReview ? 'cross_tradition_profile_review' : 'cross_tradition_profile_unit',
      unit_description: `Active-collation profile unit for ${profileUnit.targetRowLabel}`,
      start_position: start,
      end_position: end,
      chunk_relative_start: start,
      chunk_relative_end: end,
      unit_length_chars: text.length,
      unit_text_content: text,
      confidence_score: candidate?.score ?? profileUnit.score ?? 1,
      processing_metadata: {
        origin: LEDGER_CROSS_TRADITION_METHOD,
        profile_id: profile.profileId,
        projection_key: profile.projectionKey,
        profile_owner: profile.owner,
        active_collation_target: {
          book_id: profile.targetBookId,
          chapter_id: profile.targetChapterId,
          context_label: profile.targetContextLabel,
          collation_label: profile.activeCollationLabel,
        },
        source_witness_id: sourceOption.witnessId,
        source_work_id: sourceOption.workId,
        source_chapter_id: sourceOption.chapterId,
        source_context_label: sourceOption.contextLabel,
        source_unit_uuids: profileUnit.sourceUnitUuids,
        source_segment_start: profileUnit.sourceSegmentStart,
        source_segment_end: profileUnit.sourceSegmentEnd,
        candidate_id: profileUnit.candidateId,
        preview_id: candidate?.previewId ?? profileUnit.previewId ?? null,
        model_id: candidate?.modelId ?? profileUnit.modelId ?? null,
        rerank_model_id: candidate?.rerankModelId ?? profileUnit.rerankModelId ?? null,
        rerank_verdict: candidate?.rerankVerdict ?? profileUnit.rerankVerdict ?? null,
        split_method: profileUnit.splitMethod,
        needs_boundary_review: profileUnit.needsBoundaryReview,
        target_row_id: profileUnit.targetRowId,
        target_row_label: profileUnit.targetRowLabel,
        target_row_ids: candidate?.targetRowIds ?? profileUnit.targetRowIds ?? [profileUnit.targetRowId],
      },
    })
  })

  const content = contentParts.join('')
  const uploadedFile: TextFile = {
    id: existingProjectionWitness?.uploaded_file?.id || newId(`cross-tradition-file-${makeStableIdPart(sourceOption.witnessId)}`),
    name: existingProjectionWitness?.uploaded_file?.name || `${placeholderSigla}-cross-tradition.txt`,
    content,
    size: content.length,
    uploadedAt: now,
  }
  const segmentationResult: CompleteMicroSegmentationResult = {
    preprocessing_source: `cross-tradition-profile:${profile.profileId}`,
    segmentation_metadata: {
      method: LEDGER_CROSS_TRADITION_METHOD,
      profile_id: profile.profileId,
      projection_key: profile.projectionKey,
      profile_owner: profile.owner,
      active_profile: profile,
      source_witness_id: sourceOption.witnessId,
      source_work_id: sourceOption.workId,
      source_chapter_id: sourceOption.chapterId,
      source_context_label: sourceOption.contextLabel,
      target_book_id: targetBookId,
      target_chapter_id: targetChapterId,
      target_context_label: targetContextLabel,
      active_collation_label: activeCollationLabel,
      row_spine_text_id: rowSpineTextId,
      rerank_model_id: LEDGER_CROSS_TRADITION_RERANK_MODEL_ID,
      imported_candidate_count: acceptedCandidateIds.length,
      newly_imported_candidate_count: orderedCandidates.length,
      materialized_profile_unit_count: microUnits.length,
      row_ownership: 'active_collation',
      source_text_role: 'mediator',
    },
    chunk_results: [
      {
        source_chunk_id: 'cross_tradition_accepted_links',
        parent_primary_chunk: 'cross_tradition_accepted_links',
        processing_time: 0,
        processing_status: 'success',
        micro_units: microUnits,
        fallback_used: false,
        chunk_position_data: {
          start_position: 0,
          end_position: content.length,
          relative_start: 0,
          relative_end: content.length,
        },
      },
    ],
    aggregated_statistics: {
      total_chunks: 1,
      successful_chunks: 1,
      failed_chunks: 0,
      total_units: microUnits.length,
    },
    numbers: {
      cross_tradition_accepted_links: microUnits.map(unit => ({
        unit_uuid: unit.unit_uuid || unit.unit_id,
        unit_number: unit.unit_number || 0,
        unit_id: unit.unit_id,
        unit_label: unit.unit_label,
      })),
    },
  }

  return {
    sourceOption,
    profile,
    projectionKey,
    existingProjectionWitnessId: existingProjectionWitness?.id || null,
    placeholderSigla,
    witnessData: {
      sigla: placeholderSigla,
      description: [
        `Active-collation projection profile for ${sourceOption.sigla}.`,
        `Original source remains ${sourceOption.contextLabel}.`,
        `${microUnits.length} profile unit${microUnits.length === 1 ? '' : 's'} materialized from ${acceptedCandidateIds.length} accepted link${acceptedCandidateIds.length === 1 ? '' : 's'} for Ledger Safe review.`,
      ].join(' '),
      manuscript_date: sourceOption.witness.manuscript_date,
      copy_year: sourceOption.witness.copy_year,
      library: 'Cross-tradition projection',
      city: sourceOption.workTitle,
      shelfmark: profile.profileId,
      language: sourceOption.witness.language,
      witness_type: 'other',
      transcription_type: sourceOption.witness.transcription_type || 'unknown',
      coverage_status: 'partial',
      lacunose_status: sourceOption.witness.lacunose_status || 'unknown',
      uploaded_file: uploadedFile,
      segmentation_result: segmentationResult,
      last_modified: now,
      version: 1,
    },
    unitByCandidateId,
    unitByCandidateRowKey,
    acceptedCandidateIds,
  }
}

export const applyLedgerCrossTraditionCandidateMappings = ({
  rows,
  materializedWitnessId,
  candidates,
  unitByCandidateId,
  unitByCandidateRowKey,
  profile,
  nowIso = new Date().toISOString(),
}: {
  rows: AnchoredRow[]
  materializedWitnessId: string
  candidates: LedgerCrossTraditionCandidate[]
  unitByCandidateId: Map<string, { unitUuid: string; unitLabel: string; text: string }>
  unitByCandidateRowKey?: Map<string, LedgerCrossTraditionProfileUnit>
  profile?: LedgerCrossTraditionActiveProfile
  nowIso?: string
}): LedgerCrossTraditionRowApplication => {
  const accepted = candidates
    .filter(candidate => (
      candidate.reviewStatus === 'accepted'
      && (
        unitByCandidateId.has(candidate.candidateId)
        || candidate.targetRowIds.some(rowId => unitByCandidateRowKey?.has(ledgerCrossTraditionCandidateRowKey(candidate.candidateId, rowId)))
      )
    ))
    .slice()
    .sort((left, right) => {
      if (left.targetRowIds[0] !== right.targetRowIds[0]) {
        return left.targetRowIds[0].localeCompare(right.targetRowIds[0])
      }
      return right.score - left.score
    })
  const bestByRowId = new Map<string, LedgerCrossTraditionCandidate>()
  accepted.forEach(candidate => {
    candidate.targetRowIds.forEach(rowId => {
      const existing = bestByRowId.get(rowId)
      if (!existing || candidate.score > existing.score) {
        bestByRowId.set(rowId, candidate)
      }
    })
  })

  const changedRowIds: string[] = []
  const appliedCandidateIds: string[] = []
  const updatedRows = rows.map(row => {
    const candidate = bestByRowId.get(row.id)
    if (!candidate) {
      return row
    }
    const profileUnit = unitByCandidateRowKey?.get(ledgerCrossTraditionCandidateRowKey(candidate.candidateId, row.id))
    const unit = profileUnit || unitByCandidateId.get(candidate.candidateId)
    if (!unit) {
      return row
    }
    const mapping: UnitMapping = {
      witnessId: materializedWitnessId,
      unitUuid: unit.unitUuid,
      unitLabel: unit.unitLabel,
      textSnapshot: unit.text,
      confidence: candidate.score,
      translationStatus: profileUnit?.needsBoundaryReview || candidate.targetRowIds.length > 1 ? 'uncertain' : 'mapped',
      addedAt: nowIso,
    }
    changedRowIds.push(row.id)
    appliedCandidateIds.push(candidate.candidateId)
    const existingLinks = Array.isArray(row.metadata?.crossTraditionLinks)
      ? row.metadata?.crossTraditionLinks as unknown[]
      : []
    return {
      ...row,
      unitMappings: [
        ...row.unitMappings.filter(existing => existing.witnessId !== materializedWitnessId),
        mapping,
      ],
      status: profileUnit?.needsBoundaryReview || candidate.targetRowIds.length > 1 ? 'needs-review' : row.status,
      updatedAt: nowIso,
      metadata: {
        ...(row.metadata || {}),
        crossTraditionLinks: [
          ...existingLinks,
          {
            candidateId: candidate.candidateId,
            previewId: candidate.previewId,
            profileId: profile?.profileId || null,
            projectionKey: profile?.projectionKey || null,
            profileOwner: profile?.owner || LEDGER_CROSS_TRADITION_PROFILE_OWNER,
            activeCollationTarget: profile ? {
              bookId: profile.targetBookId,
              chapterId: profile.targetChapterId,
              contextLabel: profile.targetContextLabel,
              collationLabel: profile.activeCollationLabel,
            } : null,
            sourceWitnessId: candidate.sourceWitnessId,
            sourceWorkId: profile?.sourceWorkId || null,
            sourceChapterId: profile?.sourceChapterId || null,
            materializedWitnessId,
            profileUnitUuid: unit.unitUuid,
            profileUnitLabel: unit.unitLabel,
            splitMethod: profileUnit?.splitMethod || 'span-context',
            needsBoundaryReview: Boolean(profileUnit?.needsBoundaryReview || candidate.targetRowIds.length > 1),
            rowOwnership: 'active_collation',
            sourceTextRole: 'mediator',
            targetRowIds: candidate.targetRowIds,
            targetRowId: row.id,
            modelId: candidate.modelId,
            rerankModelId: candidate.rerankModelId || null,
            score: candidate.score,
            appliedAt: nowIso,
          },
        ],
      },
    }
  })

  return {
    updatedRows,
    changedRowIds,
    appliedCandidateIds: Array.from(new Set(appliedCandidateIds)),
  }
}
